import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from '../src/contexts/ThemeContext.js';
import { encrypted } from "./utils/Utility.jsx";
import useStartSession from "./hooks/useStartSession.js";
import { useDispatch } from "react-redux";
import { env } from "./environment.js";
import { toast } from "react-toastify";
import Login from "./pages/Login/Login.jsx";
import store from "./store/index.js";
import SessionExpiration from "./services/SessionExpiration.jsx";
import Home from "./pages/Home/Home.jsx";
import FAQ from "./pages/FAQ/FAQ.jsx";
import NPPAcademy from "./pages/NPPAcademy/NPPAcademy.jsx";
import Chat from "./pages/Chat/Chat.jsx";
import { TranslationProvider } from "./contexts/TranslationContext.js";
import { Provider } from "react-redux";
import FeedbackPopup from "./components/Feedback/Feedback.jsx";
import "react-toastify/dist/ReactToastify.css";
import { postShowFeedback } from "./store/apiActions.js";
import ProtectedRoute from "./services/AuthGaurd.jsx";
import { EventEmitterProvider } from "./contexts/EventEmmiter.js";
import SplashScreen from "./pages/Splash/Splash.jsx";
import { setupAxiosInterceptors } from "./api/axiosInterceptor";
import { clearLocalStoragePreservingTheme } from './utils/Utility.jsx';


function MainContent() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate()
  const startSession = useStartSession();
  const [showFeedback, setShowFeedback] = useState(false);
  const [isFeedbackPopupClosed, setIsFeedbackPopupClosed] = useState(false);
  setupAxiosInterceptors(navigate);
  
 useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/home");
      return;
    }
  }, []);
  useEffect(() => {
    const validateIsLogin = () => {
      try {
        const isLogin = encrypted.getDecryptedItem("isLogin");
        if (isLogin !== true && location?.pathname !== "/sign-in" && location?.pathname !=="/") {
          toast.error("Session tampered or expired. Please sign in again.");
          clearLocalStoragePreservingTheme();
          navigate("/sign-in");
        }
      } catch (error) {
        toast.error("Session validation failed. Please sign in again.");
        clearLocalStoragePreservingTheme();
        navigate("/sign-in");
      }
    };
  
    validateIsLogin();
    // eslint-disable-next-line
  }, [location.pathname]);
  


  useEffect(() => {
    const handleSessionStart = async () => {
    if (
      window.performance &&
      performance.getEntriesByType("navigation")[0].type === "reload" &&
      window.location.pathname !== "/sign-in"
    ) {
      localStorage.removeItem("chatbotDetails");
      localStorage.removeItem("conversation");
      localStorage.removeItem("isFeedbackPopupClosed");
      localStorage.removeItem("feedbackSubmitted");
      await startSession();
      navigate("/home");
    }
  };

  handleSessionStart();
  // eslint-disable-next-line
  }, []);


  const handleCloseFeedbackPopup = () => {
    setShowFeedback(false);
    setIsFeedbackPopupClosed(true);
    encrypted.setEncryptedItem(
      "isFeedbackPopupClosed", "true");
  };

  const checkFeedbackPopup = async () => {
    let sessionData = encrypted.getDecryptedItem(
      "startSession");
    const userId = sessionData?.user_id || "";
    const sessionId = sessionData?.session_id || "";
  
    const body = {
      user_id: userId,
      session_id: sessionId,
    };
  
    const url = `${env.baseURL}/check-feedback-popup`;
  
    try {
      const response = await dispatch(postShowFeedback(url, body));
      if (response) {
        setShowFeedback(true);
      }
    } catch (error) {
      console.error("Error checking feedback popup:", error);
      toast.error(error?.response?.data?.message || "Failed to check feedback popup.");
    }
  };
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (window.location.pathname === "/sign-in") return;
      const chatbotResponses = encrypted.getDecryptedItem(
        "chatbotDetails");
      const popupCheck = encrypted.getDecryptedItem(
        "isFeedbackPopupClosed");
      const feedbackSubmitted = localStorage.getItem("feedbackSubmitted");

      if (
        document.visibilityState === "visible" &&
        chatbotResponses?.response?.bot?.body &&
        !popupCheck &&
        !feedbackSubmitted
      ) {
        checkFeedbackPopup().then((shouldShowFeedback) => {
          if (shouldShowFeedback) {
            setShowFeedback(true);
          }
        });
      }
    };

    const handleBeforeUnload = (event) => {
      if (window.location.pathname === "/sign-in") return;
      const chatbotResponses = encrypted.getDecryptedItem(
        "chatbotDetails");
      const popupCheck = encrypted.getDecryptedItem(
        "isFeedbackPopupClosed");
      const feedbackSubmitted = localStorage.getItem("feedbackSubmitted");

      if (chatbotResponses?.response?.bot?.body && !popupCheck && !feedbackSubmitted) {
        checkFeedbackPopup().then((shouldShowFeedback) => {
          if (shouldShowFeedback) {
            setShowFeedback(true);
          }
        });
        event.preventDefault();
        event.returnValue = "";
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  });

  return (
    <div style={{ margin: 0, padding: 0, width: "100%", height: "100%" }}>
      <TranslationProvider>
        <SessionExpiration>
          <Routes>
            <Route path="/" element={<SplashScreen />} />
            <Route path="sign-in" element={<Login />} />
            <Route path="*" element={<Navigate to="sign-in" />} />
            <Route
              path="npp-academy"
              element={<ProtectedRoute element={NPPAcademy} />}
            />
            <Route
              path="home"
              element={<ProtectedRoute element={Home} />}
            />
            <Route
              path="chat"
              element={<ProtectedRoute element={Chat} />}
            />
             <Route
              path="user-guide"
              element={<ProtectedRoute element={FAQ} />}
            />
          </Routes>
        </SessionExpiration>
        {showFeedback && !isFeedbackPopupClosed && window.location.pathname !== "/sign-in" && (
          <FeedbackPopup onClose={handleCloseFeedbackPopup} />
        )}
      </TranslationProvider>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <EventEmitterProvider>
        <ThemeProvider>
        <Router>
          <MainContent />
        </Router>
        </ThemeProvider>
      </EventEmitterProvider>
    </Provider>
  );
}

export default App;